import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Calendar, MapPin, ShoppingBag, ArrowRight, Users, Bike } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsRes, listingsRes] = await Promise.all([
          axios.get(`${API_URL}/events?limit=3`),
          axios.get(`${API_URL}/listings?limit=4`),
        ]);
        setEvents(eventsRes.data);
        setListings(listingsRes.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, { 
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
      {/* Welcome Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight">
          {t('nav.home').toUpperCase()}
        </h1>
        <p className="text-muted-foreground mt-2">
          {user?.language === 'fr' 
            ? `Bienvenue, ${user?.username} !` 
            : `Welcome back, ${user?.username}!`}
        </p>
      </motion.div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { icon: Calendar, label: t('nav.events'), path: '/events', color: 'bg-orange-500/10 text-orange-500' },
          { icon: MapPin, label: t('nav.map'), path: '/map', color: 'bg-blue-500/10 text-blue-500' },
          { icon: ShoppingBag, label: t('nav.marketplace'), path: '/marketplace', color: 'bg-green-500/10 text-green-500' },
          { icon: Bike, label: t('profile.myGarage'), path: '/profile', color: 'bg-purple-500/10 text-purple-500' },
        ].map((item, index) => (
          <motion.div
            key={item.path}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Link to={item.path} data-testid={`quick-action-${item.path.slice(1)}`}>
              <Card className="group hover:border-primary/50 transition-colors cursor-pointer">
                <CardContent className="p-4 flex flex-col items-center text-center">
                  <div className={`w-12 h-12 rounded-full ${item.color} flex items-center justify-center mb-3`}>
                    <item.icon className="w-6 h-6" />
                  </div>
                  <span className="font-medium text-sm">{item.label}</span>
                </CardContent>
              </Card>
            </Link>
          </motion.div>
        ))}
      </div>

      {/* Events Section */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-bold text-xl uppercase tracking-tight flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            {t('events.title')}
          </h2>
          <Link to="/events" data-testid="view-all-events">
            <Button variant="ghost" size="sm" className="gap-1">
              {t('common.viewAll')}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-4">
                  <div className="h-32 bg-muted rounded-md mb-4" />
                  <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : events.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {events.map((event, index) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="group hover:border-primary/50 transition-colors overflow-hidden">
                  <div 
                    className="h-32 bg-cover bg-center"
                    style={{
                      backgroundImage: event.image_url 
                        ? `url('${event.image_url}')` 
                        : `url('https://images.unsplash.com/photo-1753041419705-b0795c0465ae?crop=entropy&cs=srgb&fm=jpg&q=85')`,
                    }}
                  />
                  <CardContent className="p-4">
                    <p className="text-xs text-primary font-mono mb-1">{formatDate(event.date)}</p>
                    <h3 className="font-heading font-bold text-lg uppercase line-clamp-1">{event.title}</h3>
                    <div className="flex items-center gap-2 mt-2 text-muted-foreground text-sm">
                      <MapPin className="w-4 h-4" />
                      <span className="line-clamp-1">{event.location}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-muted-foreground text-sm">
                      <Users className="w-4 h-4" />
                      <span>{event.participants_count} {t('events.participants')}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        ) : (
          <Card className="bg-card/50">
            <CardContent className="p-8 text-center">
              <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">{t('events.noEvents')}</p>
              <Link to="/events/create">
                <Button className="mt-4" data-testid="create-first-event">
                  {t('events.create')}
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </section>

      {/* Marketplace Section */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-bold text-xl uppercase tracking-tight flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-primary" />
            {t('marketplace.title')}
          </h2>
          <Link to="/marketplace" data-testid="view-all-listings">
            <Button variant="ghost" size="sm" className="gap-1">
              {t('common.viewAll')}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-0">
                  <div className="aspect-square bg-muted" />
                  <div className="p-3">
                    <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : listings.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {listings.map((listing, index) => (
              <motion.div
                key={listing.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link to={`/marketplace/${listing.id}`} data-testid={`listing-${listing.id}`}>
                  <Card className="group hover:border-primary/50 transition-colors overflow-hidden">
                    <div 
                      className="aspect-square bg-cover bg-center"
                      style={{
                        backgroundImage: listing.images?.[0] 
                          ? `url('${listing.images[0]}')` 
                          : `url('https://images.unsplash.com/photo-1629628083289-009e6f87f5cf?crop=entropy&cs=srgb&fm=jpg&q=85')`,
                      }}
                    />
                    <CardContent className="p-3">
                      <h3 className="font-medium text-sm line-clamp-1">{listing.title}</h3>
                      <p className="text-primary font-mono font-bold mt-1">
                        {listing.price.toLocaleString()} {listing.currency}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        ) : (
          <Card className="bg-card/50">
            <CardContent className="p-8 text-center">
              <ShoppingBag className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">{t('marketplace.noListings')}</p>
              <Link to="/marketplace/create">
                <Button className="mt-4" data-testid="create-first-listing">
                  {t('marketplace.createListing')}
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </section>
    </div>
  );
};

export default Dashboard;
